import { Navigate, useLocation } from 'react-router-dom';

import { hasPermissions } from '@/modules/auth/utils/hasPermissions';
import { isHiringManagerOnly } from '@/modules/auth/utils/hasRoles';
import { useCurrentTenantQuery } from '@/modules/companies/queries/useCurrentTenantQuery';
import { useCurrentPageConfig } from '@/modules/layouts/hooks/useCurrentPageConfig';
import Loader from '@/modules/ui/components/loader/Loader';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

import { AppLayout } from './AppLayout';
import { ProtectedRoute } from './ProtectedRoute';

export const ProtectedLayout = () => {
  const location = useLocation();
  const pageConfig = useCurrentPageConfig();
  const currentUserQuery = useCurrentUserQuery();
  const currentTenantQuery = useCurrentTenantQuery();

  const isLoading = currentTenantQuery.isLoading || currentUserQuery.isLoading;
  if (isLoading) {
    return <Loader />;
  }

  const hasFinishedSignupFlow =
    currentTenantQuery.data?.onboarding?.isComplete === true;

  if (!hasFinishedSignupFlow) {
    return <Navigate to="/sign-up/get-started" replace />;
  }

  // Redirect users with only hiring manager role to '/hm' routes. The generic rewrite covers /jobs/add too, which
  // HMApp forwards to a new chat because jobs are created there, and /jobs/:id/edit, in HMApp since plan 13g
  // (HMJobEdit itself falls back to the candidates view while the panel rollout flag is off).
  const userRoles = currentUserQuery.data?.roles ?? [];
  if (isHiringManagerOnly(userRoles)) {
    const hmPath = `/hm${location.pathname === '/' ? '' : location.pathname}${location.search}`;
    return <Navigate to={hmPath} replace />;
  }

  const hasUserPermissions =
    !currentUserQuery.data ||
    hasPermissions(
      pageConfig?.permissions ?? [],
      currentUserQuery.data.permissions ?? [],
    );

  if (!hasUserPermissions) {
    return <Navigate to="/permission-denied" replace />;
  }

  return (
    <ProtectedRoute>
      <AppLayout />
    </ProtectedRoute>
  );
};
